import { useEffect, useRef, useState } from "react";

import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Popover from "@mui/material/Popover";
import TextField from "@mui/material/TextField";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";

import { useTranslation } from "react-i18next";

import PlanScope from "@plannel/pages/factory-plan/common/component/PlanScope";
import planVersionService from "@plannel/services/data/plan-version-service";
import fpCommonService from "@plannel/services/fp/fp-common-service";
import dateUtils from "@plannel/utils/date-util";
import debug from "@plannel/utils/debug-util";

/**
 * old VersionSearchGroup.jsx 포팅 — Report 화면(크로스탭+차트형, §A) 공용 "시뮬레이션 버전" 조회조건.
 * fp-report-screen-guide.md §A-1b 설계 그대로: **controlled**(forwardRef/useImperativeHandle 미사용,
 * factory-plan 전체 컨벤션과 동일), 그룹은 자기 도메인(계획수립일→버전→plant 캐스케이드)만 소유하고
 * 화면을 넘나드는 캐스케이드(버전→기간 재계산 등)는 화면이 소유한다(§A-1b 참고, 여기서 하지 않음).
 *
 * props:
 *  - value: { planScope, planningDate, versionCd, plantCd, selectedVersion }
 *  - onChange: (partial) => void — 캐스케이드 각 단계 결과를 병합해서 알려줌
 *
 * 화면 착수 조회(old `initializedWithState`)는 이 컴포넌트가 신호를 주는 방식(예전 `onReady` prop)이
 * 아니라, 화면이 `value.versionCd`(또는 관련 필드) 변경을 직접 관찰하는 `useEffect`로 처리한다
 * (조회 버튼을 없앤 뒤로는 "최초 진입 자동조회"와 "이후 값 변경 시 자동조회"가 같은 트리거이므로
 * 굳이 따로 구분할 필요가 없다 — `fp-report-screen-guide.md` §A-9b).
 *
 * TODO(MIG-SKELETON): 아래 항목은 스켈레톤 단계에서 비워둔 것 — 화면 실제 연결 시 채운다.
 *  - old `highlightDates`(버전 있는 날짜 시각 강조, 선택 자체는 막지 않음) 대신 지금은
 *    `shouldDisableDate`(버전 없는 날짜는 선택 자체를 막음)로 단순화했다 — old보다 더 엄격한
 *    동작이라 실제 데이터로 확인 후 필요하면 강조만 하는 방식으로 되돌린다.
 */
/**
 * AI 시나리오 격리 버전의 versionCd 집합.
 *
 * FP 는 [D-007] 이중 행이라 버전 1건이 z_plan_version(호스트) + tb_fp_plan_version(엔진·화면)
 * 두 행으로 존재한다. "AI 가 돌린 임시 버전"이라는 사실은 **z 쪽에만** 있고
 * (module_cd='FP' · name="{planScope}:{versionCd}" · delFlg='Y'), FP 버전 목록 API 는
 * tb 쪽만 읽으므로 그 사실을 모른다. 그래서 여기서 두 목록을 대조해 거른다.
 *
 * ★ MP·DP 와 같은 방식이다 — 그쪽도 백엔드가 아니라 프런트가 delFlg 로 거른다
 *   (Home.js 의 live 필터, AiDpScenarioPlanning.js 의 "AI 시뮬 격리 버전(delFlg='Y')은 base 가 아니다").
 *   백엔드는 delFlg 로 거르지 않는다 — 그건 '릴리즈 시 삭제' lock flg 다
 *   (saas-application PlanVersionQueryRepository 의 makeWhereClause 주석).
 *
 * ⚠️ 조회가 실패하면 **빈 집합**을 돌려준다. 즉 못 걸러서 시나리오 버전이 보일 수는 있어도,
 *    버전 목록 자체가 비어 화면이 죽는 일은 없다 — 은닉은 편의고 목록은 기능이다.
 */
const fetchAiScenarioVersionCds = async (planScope) => {
  try {
    const res = await planVersionService.getAll({ moduleCd: "FP" });
    const rows = res?.data?.results ?? [];
    // ⚠️ delFlg 표현이 화면마다 다르다 — Home.js 는 boolean(`!v.delFlg`), AiDpScenarioPlanning.js 는
    //    문자열("Y") 비교다. FP 는 PlanVersion.toDto() 경로라 boolean 이지만, 한쪽만 보면
    //    조용히 안 걸러지거나(문자열인데 boolean 비교) 전부 걸러진다(boolean 인데 "N" 문자열 비교).
    const isHidden = (z) => z.delFlg === true || z.delFlg === "Y";
    return new Set(
      rows
        .filter(isHidden)
        .map(z => {
          // name = "{planScope}:{versionCd}" — planScope 가 일치하는 것만 센다.
          const idx = String(z.name ?? "").indexOf(":");
          if (idx < 0) return null;
          return String(z.name).slice(0, idx) === planScope ? String(z.name).slice(idx + 1) : null;
        })
        .filter(Boolean)
    );
  } catch (e) {
    debug(e);
    return new Set();
  }
};

const VersionSearchGroup = ({ value, onChange }) => {
  const { t } = useTranslation();

  const inputBoxRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [popoverOpen, setPopoverOpen] = useState(false);

  // old의 모듈 레벨 versionPlants/versionData(여러 화면 인스턴스가 공유하던 잠재 버그, §A-1b)와
  // 달리 인스턴스별로 격리 — 여러 탭에서 동시에 열어도 서로 덮어쓰지 않는다.
  const versionPlantsRef = useRef({});
  const versionListRef = useRef([]);
  const [dateWithVersions, setDateWithVersions] = useState([]);
  const [versionOptions, setVersionOptions] = useState([]);
  const [plantOptions, setPlantOptions] = useState([]);

  const openPopover = () => setPopoverOpen(true);
  const closePopover = () => setPopoverOpen(false);

  useEffect(() => {
    setAnchorEl(inputBoxRef.current);
  }, []);

  const isSameDate = (a, b) => a && b && dateUtils.formatDate(a, "yyyy-MM-dd") === dateUtils.formatDate(b, "yyyy-MM-dd");

  // 1단계 — planScope 변경 → plan-dts 조회 → 현재 planningDate가 새 목록에도 있는지 검증한다.
  // 그대로 들고 있지 않는다 — 없으면(목록이 비어도 포함) 목록의 첫 값(없으면 null)으로 되돌린다.
  // 버전/plant는 여기서 건드리지 않는다 — planningDate가 바뀌면 아래 2단계 effect가 그 변경을
  // 감지해서 항상 다시 계산해준다(2단계가 planScope도 의존성에 갖고 있어 이 값이 그대로여도
  // 최소 한 번은 재조회된다).
  useEffect(() => {
    if (!value?.planScope) return;

    let cancelled = false;

    (async () => {
      try {
        const planDtsRes = await fpCommonService.getPlanDts({ planScope: value.planScope });
        const dates = (planDtsRes.data ?? []).map(d => new ZDate(d));
        if (cancelled) return;
        setDateWithVersions(dates);

        const stillValid = dates.some(d => isSameDate(d, value.planningDate));
        if (!stillValid) {
          onChange({ planningDate: dates[0] ?? null });
        }
      } catch (e) {
        debug(e);
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value?.planScope]);

  // 2단계 — planScope/planningDate 변경(1단계의 자동 리셋뿐 아니라 사용자가 팝오버에서 직접
  // 계획수립일을 바꾼 경우도 포함) → 버전 목록을 다시 조회하고 versionCd/plantCd/selectedVersion을
  // 항상 다시 계산한다. 목록이 비면 셋 다 빈 값으로 되돌린다(이전 선택을 그대로 두지 않는다).
  useEffect(() => {
    if (!value?.planScope || !value?.planningDate) {
      versionListRef.current = [];
      versionPlantsRef.current = {};
      setVersionOptions([]);
      setPlantOptions([]);
      if (value?.versionCd || value?.plantCd?.length || value?.selectedVersion) {
        onChange({ versionCd: "", plantCd: [], selectedVersion: null });
      }
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const [versionsRes, hiddenCds] = await Promise.all([
          fpCommonService.getVersions({
            planScope: value.planScope,
            planningDate: dateUtils.formatDate(value.planningDate, "yyyyMMdd"),
          }),
          fetchAiScenarioVersionCds(value.planScope),
        ]);
        if (cancelled) return;

        // AI 시나리오 격리 버전은 목록에서 뺀다 — MP/DP 와 같은 방식(프런트가 z 의 delFlg 로 거른다).
        const versions = (versionsRes.data ?? []).filter(v => !hiddenCds.has(v.versionCd));
        versionListRef.current = versions;
        versionPlantsRef.current = {};
        versions.forEach(v => {
          versionPlantsRef.current[v.versionCd] = (v.plants ?? []).map(p => ({ value: p.plantCd, label: p.plantNm }));
        });
        setVersionOptions(versions.map(v => ({ value: v.versionCd, label: `${v.versionCd} : ${v.descTxt ?? ""}` })));

        const isVersionValid = versions.some(v => v.versionCd === value.versionCd);
        const versionCd = isVersionValid ? value.versionCd : (versions[0]?.versionCd ?? "");
        const selectedVersion = versions.find(v => v.versionCd === versionCd) ?? null;
        const plants = versionPlantsRef.current[versionCd] ?? [];
        setPlantOptions(plants);
        const plantCd = isVersionValid && value.plantCd?.length ? value.plantCd : plants.map(p => p.value);

        onChange({ versionCd, plantCd, selectedVersion });
      } catch (e) {
        debug(e);
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value?.planScope, value?.planningDate]);

  const handleVersionChange = (versionCd) => {
    const selectedVersion = versionListRef.current.find(v => v.versionCd === versionCd) ?? null;
    const plants = versionPlantsRef.current[versionCd] ?? [];
    setPlantOptions(plants);
    onChange({ versionCd, selectedVersion, plantCd: plants.map(p => p.value) });
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <PlanScope value={value?.planScope} onChange={(planScope) => onChange({ planScope })} />
      <TextField
        size="small"
        label={t("SIMULATION_VERSION")}
        value={value?.versionCd ?? ""}
        inputRef={inputBoxRef}
        onClick={openPopover}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              <IconButton size="small" onClick={openPopover}>
                {popoverOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              </IconButton>
            </InputAdornment>
          ),
        }}
        // 220→250: 버전 코드(FP-YYYYMMDD-NNN-NNN 형태)가 길어 220에서 다소 좁아 보임(2026-08-10).
        // ItemAutocomplete 등 다른 모듈의 autocomplete류 조회조건이 쓰는 230~250 티어에 맞춘다
        // (MP reports 4개 화면이 전부 250, RP create-plan이 240 — 2026-08-12 전 모듈 조사).
        // minWidth가 아니라 width로 고정하는 이유: minWidth는 하한선이라 라벨("시뮬레이션 버전")+
        // 버전코드+드롭다운 아이콘의 내용 폭이 그보다 넓으면 실제 렌더 폭이 278px까지 늘어난다
        // (2026-08-12 사용자가 실측으로 발견). 250에 정확히 맞추려면 width로 고정해야 한다.
        sx={{ ml: 1.5, width: 250 }}
      />
      <Popover
        anchorEl={anchorEl}
        open={popoverOpen}
        onClose={closePopover}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2, width: 300 }}>
            <DatePicker
              label={t("FP_LABEL_PLANNING_DATE")}
              format="yyyy-MM-dd"
              value={value?.planningDate ?? null}
              shouldDisableDate={(date) => !dateWithVersions.some(d => dateUtils.formatDate(d, "yyyy-MM-dd") === dateUtils.formatDate(date, "yyyy-MM-dd"))}
              onAccept={(date) => onChange({ planningDate: date })}
              slotProps={{ textField: { size: "small" } }}
            />
            {/* old는 MUI Select + renderValue={(selected) => selected}라 **닫힌 입력칸엔 versionCd만**
                보이고 펼친 목록에만 "코드 : 설명"이 나왔다(old VersionSearchGroup.jsx:313). Autocomplete는
                getOptionLabel이 입력칸 표시와 목록 표시를 겸해서 그대로 두면 입력칸에도 설명이 딸려온다 —
                getOptionLabel은 코드만 반환하고 목록 렌더링만 renderOption으로 분리해 old와 동일하게 맞춘다.
                filterOptions는 label(코드+설명) 기준이라 설명 텍스트로도 검색된다(Select였던 old엔 없던
                기능이지만 Autocomplete로 대체하며 자연히 생긴 것 — 코드만으로 필터되면 오히려 퇴보). */}
            <Autocomplete
              size="small"
              disableClearable
              options={versionOptions}
              getOptionLabel={(o) => o?.value ?? ""}
              renderOption={(props, option) => <li {...props} key={option.value}>{option.label}</li>}
              filterOptions={createFilterOptions({ stringify: (o) => o.label })}
              isOptionEqualToValue={(o, v) => o.value === v.value}
              value={versionOptions.find(o => o.value === value?.versionCd) ?? null}
              onChange={(_, option) => handleVersionChange(option?.value ?? "")}
              renderInput={(params) => <TextField {...params} label={t("SIMULATION_VERSION")} />}
            />
            <Autocomplete
              multiple
              size="small"
              options={plantOptions}
              getOptionLabel={(o) => o?.label ?? ""}
              isOptionEqualToValue={(o, v) => o.value === v.value}
              value={plantOptions.filter(o => (value?.plantCd ?? []).includes(o.value))}
              onChange={(_, options) => onChange({ plantCd: options.map(o => o.value) })}
              renderInput={(params) => <TextField {...params} label={t("FP_VERSION_PLANT")} />}
            />
          </Box>
        </LocalizationProvider>
      </Popover>
    </Box>
  );
};

export default VersionSearchGroup;
